// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatDialog } from '@angular/material';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { ToastrService } from 'ngx-toastr';
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from 'rxjs/operators';

// Core packages
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CommunicationsService } from 'src/app/modules/communications/communications.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ViewCommunicationDetailDialogComponent } from 'src/app/shared/components/dialogs/view-communication-detail-dialog/view-communication-detail-dialog.component';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { CommunicationsDataSource } from 'src/app/shared/utils/data-sources/communications-data-source';

@Component({
  selector: 'app-communications-list',
  templateUrl: './communications-list.page.html',
  styleUrls: ['./communications-list.page.scss']
})
export class CommunicationsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  // BEGIN - Table properties
  displayedColumns: string[] = ['index', 'attachments', 'subject', 'email', 'createdAt', 'actions'];
  dataSource: CommunicationsDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  attachmentsFilter: FormControl = new FormControl('');
  emailFilter: FormControl = new FormControl('');
  subjectFilter: FormControl = new FormControl('');
  createdAtFilter: FormControl = new FormControl('');
  // END - Table properties

  communicationIsDeleting = false;
  // createdCommunication: boolean;
  chipSelected = false;

  /**
   * Class constructor
   */
  constructor(
    public openMessageDialog: MatDialog,
    public confirmDialog: MatDialog,
    private communicationsService: CommunicationsService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private toastrService: ToastrService,
    private configService: ConfigService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('communications.list.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // this.subscriptions.push(this.communicationsService.createdCommunication$.subscribe(res => this.createdCommunication = res));

    // Init data source
    this.dataSource = new CommunicationsDataSource(this.communicationsService, this.helperService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('communications');

    // Subscribe to new added communications (they're added from the sidenav)
    this.subscriptions.push(
      this.communicationsService.createdCommunication$.subscribe(
        res => {
          if (res) {
            this.loadPage();
          }
        }
      )
    );
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.emailFilter.valueChanges.pipe(startWith(null as string)),
        this.attachmentsFilter.valueChanges.pipe(startWith(null as string)),
        this.subjectFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.paginator.pageIndex = 0;
              this.loadPage();
            }
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'communications',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        attachments: this.attachmentsFilter.value,
        email: this.emailFilter.value,
        subject: this.subjectFilter.value,
        createdAt: this.createdAtFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      attachments: this.attachmentsFilter.value,
      email: this.emailFilter.value,
      subject: this.subjectFilter.value,
      createdAt: this.createdAtFilter.value ? new Date(this.createdAtFilter.value).toISOString() : undefined,
    };
    this.dataSource.loadItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export users in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('communications.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.communicationsService.getExport().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.attachmentsFilter.setValue(null);
    this.emailFilter.setValue(null);
    this.subjectFilter.setValue(null);
    this.createdAtFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = 'desc';
    this.sort.active = 'createdAt';
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.attachments) {
                  this.attachmentsFilter.setValue(setting.search.attachments);
                }
                if (setting.search && setting.search.email) {
                  this.emailFilter.setValue(setting.search.email);
                }
                if (setting.search && setting.search.subject) {
                  this.subjectFilter.setValue(setting.search.subject);
                }
                if (setting.search && setting.search.createdAt) {
                  this.createdAtFilter.setValue(setting.search.createdAt);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispatch a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }

  /**
   * Open communication detail dialog
   */
  onViewCommunicationDetail(element: any): void {
    if (!element) {
      return;
    }

    // Open dialog
    this.openMessageDialog.open(ViewCommunicationDetailDialogComponent, {
      minWidth: '60%',
      maxHeight: '98vh',
      data: element,
    });
  }

  /**
   * Remove communication
   */
  onRemoveCommunication(element: any): void {
    if (!element) {
      return;
    }

    const dialogRef = this.confirmDialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.confirm.title'),
        message: this.translateService.instant('communications.list.deleteMessage'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.undo'),
      },
      width: '500px',
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res => {
          if (!res) {
            return;
          }
          this.communicationIsDeleting = true;
          this.subscriptions.push(
            this.communicationsService.delete(element._id)
              .pipe(
                finalize(() => this.communicationIsDeleting = false)
              )
              .subscribe(
                res2 => {
                  this.toastrService.success(res2.message);
                  this.loadPage();
                  return;
                },
                err => {
                  // Choose one of the following error handling
                  // method. The first one shows a message right
                  // under the form fields (if the form is properly
                  // setted), the second one shows toastr
                  // notifications for each error
                  // this.helperService.handleFormError(this.passwordForm, err);
                  this.helperService.handleError(err);
                }
              )
          );
        }
      );
  }

  // onClickRecipientFiltering(recipient: any) {
  //   this.emailFilter.setValue(recipient.email);
  // }
}
